import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { CreditCard, Landmark, Truck, Wallet, Store, Car } from "lucide-react";
import { api, eur, formatApiError } from "@/lib/api";
import { useShop } from "@/context/ShopContext";
import { CartTimer } from "@/components/shop/CartTimer";
import { AddressInput } from "@/components/shop/AddressInput";
import { SavedAddressPicker, pickAddress } from "@/components/shop/SavedAddressPicker";
import { ShippingSummary } from "@/components/shop/ShippingSummary";
import { useAuth } from "@/context/AuthContext";
import { useCurrency } from "@/lib/currency";
import { InvoiceRequest, isInvoiceValid } from "@/components/shop/InvoiceRequest";
import { EmailVerifyBanner } from "@/pages/VerifyEmailPage";
import { fieldRules } from "@/lib/validation";
import { EU_COUNTRIES, PAYMENT_METHODS, B2B_PAYMENT_METHODS, VAT_RATE, SICILY_PROVINCES } from "@/lib/constants";
import { useShippingConfig, euro, shipFee, promoText } from "@/lib/shipping";
import { ProductImage } from "@/components/shop/ProductImage";

const ICONS = { card: CreditCard, paypal: Wallet, bonifico: Landmark, contrassegno: Truck, negozio: Store };
const FIELDS = [
  ["full_name", "Nome e cognome", "sm:col-span-2"], ["address", "Indirizzo e numero civico", "sm:col-span-2"],
  ["city", "Città", ""], ["zip", "CAP", ""], ["province", "Provincia", ""], ["phone", "Telefono", ""],
];

export default function CheckoutPage() {
  const { cart, cartTotal, clearCart } = useShop();
  const { user, checkAuth } = useAuth();
  const navigate = useNavigate();
  const currency = useCurrency();
  const b2b = user?.account_type === "b2b";
  const guest = user === false;
  const [guestEmail, setGuestEmail] = useState("");
  const [ship, setShip] = useState({ full_name: user?.name || "", address: "", city: "", zip: "", province: "", phone: "", country: "Italia" });
  const [method, setMethod] = useState(b2b ? "bonifico" : "card");
  const [accept, setAccept] = useState(false);
  const [invoiceReq, setInvoiceReq] = useState(null);
  const [notes, setNotes] = useState("");
  const [busy, setBusy] = useState(false);
  const [delivery, setDelivery] = useState("spedizione");
  const [useCredit, setUseCredit] = useState(false);
  const [quote, setQuote] = useState(null);
  const [saved, setSaved] = useState([]);
  const [selected, setSelected] = useState(null);
  const [saveToProfile, setSaveToProfile] = useState(true);

  useEffect(() => {
    if (!user) return;
    api.get("/profile").then(({ data }) => {
      setSaved(data.addresses);
      const def = data.addresses.find((a) => a.default) || data.addresses[0];
      if (def) { setShip(pickAddress(def)); setSelected(def.id); setSaveToProfile(false); }
      else setShip((s) => ({ ...s, full_name: s.full_name || `${data.first_name} ${data.last_name}`.trim(), phone: s.phone || data.phone || "" }));
    }).catch(() => {});
  }, [user]); // eslint-disable-line react-hooks/exhaustive-deps
  const editShip = (patch) => { setShip((s) => ({ ...s, ...patch })); if (selected) { setSelected(null); setSaveToProfile(true); } };
  const sicily = SICILY_PROVINCES.includes(ship.province.trim().toUpperCase());
  const italy = ship.country === "Italia";
  const codAllowed = delivery === "spedizione" && italy && !cart.some((i) => i.preorder);
  const sc = useShippingConfig();
  const shipping = delivery === "ritiro" ? 0 : delivery === "privata" ? (quote?.fee ?? 0) : shipFee(sc, italy ? "italy" : "eu", cartTotal);
  const cod = method === "contrassegno" ? sc.cod_fee : 0;
  const vat = b2b ? Math.round((cartTotal + shipping) * VAT_RATE) / 100 : 0;
  const gross = cartTotal + shipping + cod + vat;
  const credit = useCredit && !b2b ? Math.min(user?.store_credit || 0, gross) : 0;
  const total = gross - credit;
  const pre = cart.find((i) => i.preorder);
  const deposit = pre?.preorder_mode === "caparra" ? Math.round(total * pre.deposit_pct) / 100 : null;

  useEffect(() => {
    setQuote(null);
    if (delivery !== "privata" || !sicily || ship.address.length < 3 || ship.city.length < 2 || ship.zip.length < 4) return undefined;
    const t = setTimeout(() => {
      api.post("/shipping/private-quote", { address: ship.address, city: ship.city, zip: ship.zip, province: ship.province, subtotal: cartTotal })
        .then((r) => setQuote(r.data)).catch((e) => setQuote({ error: formatApiError(e) }));
    }, 700);
    return () => clearTimeout(t);
  }, [delivery, sicily, ship.address, ship.city, ship.zip, ship.province, cartTotal]);
  useEffect(() => {
    if (delivery === "privata" && !sicily) setDelivery("spedizione");
  }, [delivery, sicily]);

  if (cart.length === 0)
    return (
      <div className="py-32 text-center" data-testid="checkout-empty">
        <p className="text-stone-600">Il carrello è vuoto.</p>
        <Link to="/catalogo" className="mt-4 inline-block text-amber-600">Vai al catalogo →</Link>
      </div>
    );

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const { data } = await api.post("/orders", {
        items: cart.map((i) => ({ product_id: i.product_id, quantity: i.quantity })),
        shipping: ship, payment_method: method, notes, origin_url: window.location.origin, delivery, use_credit: useCredit && !b2b,
        lang: localStorage.getItem("cs_lang") || "it", accept_terms: accept, invoice_request: b2b ? null : invoiceReq, save_to_profile: !guest && saveToProfile && !selected,
        guest_email: guest ? guestEmail.trim() : null,
      });
      clearCart();
      if (credit > 0) checkAuth();
      if (data.checkout_url) {
        window.location.href = data.checkout_url;
        return;
      }
      navigate(`/ordine/${data.order.id}?nuovo=1${data.order.access_token ? `&t=${data.order.access_token}` : ""}`);
    } catch (err) {
      toast.error(formatApiError(err));
      setBusy(false);
    }
  };

  return (
    <form onSubmit={submit} className="mx-auto grid max-w-[1440px] gap-10 px-4 py-10 sm:px-6 lg:grid-cols-[1fr_420px] lg:px-12">
      <div className="space-y-10">
        <h1 className="font-display text-3xl font-bold tracking-tight sm:text-4xl">Cassa</h1>
        <EmailVerifyBanner testid="checkout-verify-banner" />
        <CartTimer testid="checkout-cart-timer" />
        {guest && (
          <section data-testid="checkout-guest">
            <h2 className="font-display text-lg font-bold">La tua email</h2>
            <input required type="email" value={guestEmail} onChange={(e) => setGuestEmail(e.target.value)} placeholder="nome@esempio.it" autoComplete="email" data-testid="checkout-guest-email"
              className="mt-3 h-12 w-full rounded-xl border border-black/10 bg-[#FFFFFF] px-4 text-sm outline-none focus:border-amber-500/60 sm:max-w-md" />
            <p className="mt-2 text-xs text-stone-600">Ti mandiamo qui conferma e aggiornamenti dell'ordine. Non serve un account: <Link to="/accedi?next=/checkout" className="font-semibold text-amber-700 underline" data-testid="checkout-login-link">accedi</Link> solo se vuoi lo storico ordini e la lista desideri.</p>
          </section>
        )}
        <section>
          <h2 className="font-display text-lg font-bold">1. Consegna</h2>
          <ShippingSummary className="mt-2" testid="checkout-shipping-summary" />
          <div className="mt-4 grid gap-3 sm:grid-cols-3">
            {[["spedizione", "Corriere nazionale", `Italia ${euro(sc.italy_fee)}${promoText(sc, "italy") ? ` (${promoText(sc, "italy")})` : ""} · UE ${euro(sc.eu_fee)}${promoText(sc, "eu") ? ` (${promoText(sc, "eu")})` : ""}`, Truck], ["privata", "Consegna privata Sicilia", sicily ? "5 € + 1 €/km da Ragusa · sconti fino al 100%" : "Disponibile solo per indirizzi in Sicilia", Car], ["ritiro", "Ritiro in negozio", "Gratuito · ti avvisiamo quando è pronto", Store]].map(([v, l, d, Icon]) => (
              <button type="button" key={v} disabled={v === "privata" && !sicily} onClick={() => { setDelivery(v); if (v !== "spedizione" && method === "contrassegno") setMethod("card"); if (v !== "ritiro" && method === "negozio") setMethod("bonifico"); if (v === "privata") setShip((s) => ({ ...s, country: "Italia" })); }} data-testid={`delivery-${v}`}
                className={`flex gap-4 rounded-2xl border-2 p-5 text-left transition-colors disabled:cursor-not-allowed disabled:opacity-50 ${delivery === v ? "border-black bg-amber-500/15" : "border-black/10 bg-white hover:border-black/30"}`}>
                <Icon className={`h-6 w-6 shrink-0 ${delivery === v ? "text-amber-600" : "text-stone-500"}`} />
                <span><span className="block font-semibold">{l}</span><span className="mt-1 block text-xs text-stone-600">{d}</span></span>
              </button>
            ))}
          </div>
          <SavedAddressPicker addresses={saved} selected={selected} onSelect={(a) => { setShip(pickAddress(a)); setSelected(a.id); setSaveToProfile(false); }}
            onNew={() => { setSelected(null); setSaveToProfile(true); setShip((s) => ({ ...s, address: "", city: "", zip: "", province: "", country: "Italia" })); }} />
          <div className="mt-4 grid gap-4 sm:grid-cols-2">
            {FIELDS.filter(([k]) => delivery !== "ritiro" || ["full_name", "phone"].includes(k)).map(([k, label, span]) => (
              <label key={k} className={`space-y-1.5 ${span}`}>
                <span className="text-sm text-stone-600">{label}</span>
                {k === "address" ? (
                  <AddressInput value={ship.address} onChange={(v) => editShip({ address: v })} sicily={delivery === "privata"} city={ship.city} enabled={ship.country === "Italia"}
                    onPick={(it) => editShip({ address: it.address, ...(it.city && { city: it.city }), ...(it.zip && { zip: it.zip }), ...(it.province && { province: it.province }) })}
                    inputProps={{ required: true, "data-testid": "checkout-address-input", ...fieldRules(k, ship.country), autoComplete: "off",
                      className: "h-12 w-full rounded-xl border border-black/10 bg-[#FFFFFF] px-4 text-sm outline-none invalid:[&:not(:placeholder-shown)]:border-rose-400 focus:border-amber-500/60" }} />
                ) : (
                <input required placeholder=" " value={ship[k]} onChange={(e) => editShip({ [k]: e.target.value })} data-testid={`checkout-${k}-input`}
                  {...fieldRules(k, ship.country)}
                  className="h-12 w-full rounded-xl border border-black/10 bg-[#FFFFFF] px-4 text-sm outline-none invalid:[&:not(:placeholder-shown)]:border-rose-400 focus:border-amber-500/60" />
                )}
              </label>
            ))}
            {delivery === "spedizione" && (
              <label className="space-y-1.5 sm:col-span-2">
                <span className="text-sm text-stone-600">Paese</span>
                <select value={ship.country} onChange={(e) => { editShip({ country: e.target.value }); if (e.target.value !== "Italia" && method === "contrassegno") setMethod("card"); }} data-testid="checkout-country-select"
                  className="h-12 w-full rounded-xl border border-black/10 bg-white px-4 text-sm outline-none focus:border-amber-500/60">
                  {EU_COUNTRIES.map((c) => <option key={c}>{c}</option>)}
                </select>
              </label>
            )}
            {!selected && !guest && (
              <label className="flex items-center gap-2 text-sm sm:col-span-2">
                <input type="checkbox" checked={saveToProfile} onChange={(e) => setSaveToProfile(e.target.checked)} data-testid="checkout-save-profile" />
                Salva nel profilo per i prossimi ordini
              </label>
            )}
            <label className="space-y-1.5 sm:col-span-2">
              <span className="text-sm text-stone-600">Note per l'ordine (facoltativo)</span>
              <textarea value={notes} onChange={(e) => setNotes(e.target.value)} rows={2} data-testid="checkout-notes-input" className="w-full rounded-xl border border-black/10 bg-[#FFFFFF] p-3 text-sm outline-none focus:border-amber-500/60" />
            </label>
          </div>
          {!b2b && <InvoiceRequest ship={ship} value={invoiceReq} onChange={setInvoiceReq} />}
        </section>
        <section>
          <h2 className="font-display text-lg font-bold">2. Metodo di pagamento</h2>
          {["card", "paypal"].includes(method) && total > 0 && <p className="mt-2 text-xs text-stone-600" data-testid="pay-window-note">Dopo la conferma hai <b>10 minuti</b> per completare il pagamento: poi l'ordine viene annullato e i prodotti tornano disponibili.</p>}
          {total <= 0 && <p className="mt-4 rounded-xl bg-violet-500/10 p-4 text-sm font-semibold text-violet-500" data-testid="credit-covers-total">Il credito negozio copre l'intero importo: nessun pagamento necessario.</p>}
          <div className={`mt-4 grid gap-3 sm:grid-cols-2 ${total <= 0 ? "hidden" : ""}`} data-testid="payment-methods">
            {(b2b ? B2B_PAYMENT_METHODS.filter((m) => delivery === "ritiro" || m.value !== "negozio") : PAYMENT_METHODS.filter((m) => codAllowed || m.value !== "contrassegno")).map((m) => {
              const Icon = ICONS[m.value];
              const on = method === m.value;
              return (
                <button type="button" key={m.value} onClick={() => setMethod(m.value)} data-testid={`payment-option-${m.value}`}
                  className={`flex gap-4 rounded-2xl border p-5 text-left transition-colors ${on ? "border-amber-500 bg-amber-500/10" : "border-black/10 bg-[#FFFFFF] hover:border-black/25"}`}>
                  <Icon className={`h-6 w-6 shrink-0 ${on ? "text-amber-600" : "text-stone-600"}`} />
                  <span>
                    <span className="block font-semibold">{m.label}</span>
                    <span className="mt-1 block text-xs text-stone-600">{m.desc}</span>
                  </span>
                </button>
              );
            })}
          </div>
        </section>
      </div>
      <aside className="h-fit rounded-2xl border border-black/10 bg-[#FFFFFF] p-6 lg:sticky lg:top-28" data-testid="order-summary">
        <h2 className="font-display text-xl font-bold">Riepilogo</h2>
        <div className="mt-4 max-h-72 space-y-3 overflow-y-auto">
          {cart.map((i) => (
            <div key={i.product_id} className="flex items-center gap-3">
              <ProductImage src={i.image} w={300} width={44} height={56} className="h-14 w-11 rounded-md object-cover" />
              <p className="flex-1 text-sm"><span className="line-clamp-1">{i.name}</span><span className="text-stone-500">× {i.quantity}</span></p>
              <span className="font-mono text-sm">{eur(i.price * i.quantity)}</span>
            </div>
          ))}
        </div>
        <div className="mt-6 space-y-2 border-t border-black/10 pt-4 text-sm">
          <div className="flex justify-between text-stone-600"><span>Subtotale</span><span className="font-mono" data-testid="summary-subtotal">{eur(cartTotal)}</span></div>
          <div className="flex justify-between text-stone-600"><span>Spedizione</span><span className="font-mono" data-testid="summary-shipping">{delivery === "privata" && !quote?.fee && quote?.fee !== 0 ? "—" : shipping ? eur(shipping) : "Gratis"}</span></div>
          {delivery === "privata" && (
            <p className={`rounded-lg p-2 text-xs ${quote?.error ? "bg-rose-500/10 text-rose-600" : "bg-black/5 text-stone-600"}`} data-testid="private-quote">
              {quote?.error ? quote.error : quote ? `${quote.km} km da Ragusa · tariffa ${eur(quote.base_fee)}${quote.discount_pct ? ` · sconto ${quote.discount_pct}%` : ""}` : "Inserisci via, città e CAP per calcolare la tariffa"}
            </p>
          )}
          {cod > 0 && <div className="flex justify-between text-stone-600"><span>Supplemento contrassegno</span><span className="font-mono" data-testid="summary-cod">{eur(cod)}</span></div>}
          {b2b && <div className="flex justify-between text-stone-600"><span>Imponibile</span><span className="font-mono" data-testid="summary-net">{eur(cartTotal + shipping)}</span></div>}
          {b2b && <div className="flex justify-between text-stone-600"><span>IVA {VAT_RATE}%</span><span className="font-mono" data-testid="summary-vat">{eur(vat)}</span></div>}
          {!b2b && user?.store_credit > 0 && (
            <label className="flex cursor-pointer items-center justify-between gap-2 rounded-xl bg-violet-500/10 p-3 text-violet-500" data-testid="use-credit-label">
              <span className="flex items-center gap-2"><input type="checkbox" checked={useCredit} onChange={(e) => setUseCredit(e.target.checked)} data-testid="use-credit-checkbox" />Usa credito negozio ({eur(user.store_credit)})</span>
              {credit > 0 && <span className="font-mono" data-testid="summary-credit">-{eur(credit)}</span>}
            </label>
          )}
          <div className="flex justify-between pt-2 font-display text-xl font-bold"><span>Totale</span><span className="font-mono text-amber-600" data-testid="summary-total">{eur(total)}</span></div>
          {currency === "USD" && <p className="text-xs text-stone-500" data-testid="currency-note">Il pagamento avviene sempre in euro: l'importo in dollari è indicativo.</p>}
          <p className="text-xs text-stone-500">{b2b ? <>Ordine B2B · <span data-no-translate>{user.b2b?.company} · P.IVA {user.b2b?.vat}</span></> : "IVA inclusa"}</p>
          {pre && (
            <p className="rounded-lg bg-violet-500/10 p-3 text-xs font-semibold text-violet-500" data-testid="summary-preorder">
              Preordine · uscita {new Date(`${pre.release_date}T00:00:00`).toLocaleDateString("it-IT")}<br />
              {deposit !== null ? `Paghi ora la caparra del ${pre.deposit_pct}%: ${eur(deposit)} · saldo ${eur(total - deposit)} all'uscita` : "Pagamento totale anticipato"}
            </p>
          )}
        </div>
        <label className="mt-6 flex items-start gap-2 text-xs text-stone-700">
          <input type="checkbox" required checked={accept} onChange={(e) => setAccept(e.target.checked)} className="mt-0.5" data-testid="checkout-accept-terms" />
          <span>Ho letto e accetto le <Link to="/condizioni-di-vendita" target="_blank" className="text-cyan-700 underline">Condizioni di vendita</Link>{!b2b && ", incluse le informazioni sul diritto di recesso"} e l'<Link to="/privacy" target="_blank" className="text-cyan-700 underline">Informativa privacy</Link>. L'ordine comporta l'obbligo di pagamento.</span>
        </label>
        <button disabled={busy || user?.email_verified === false || !accept || !isInvoiceValid(invoiceReq) || (delivery === "privata" && !(quote && !quote.error))} data-testid="place-order-button" className="mt-4 h-14 w-full rounded-full bg-amber-500 font-display text-lg font-bold text-black transition-colors hover:bg-amber-400 disabled:opacity-60">
          {busy ? "Elaborazione..." : total > 0 && (method === "card" || method === "paypal") ? "Procedi al pagamento" : "Conferma ordine"}
        </button>
      </aside>
    </form>
  );
}
